<template>
  <div class="mb-3">
    <label v-if="label" :for="id" class="form-label">{{ label }}</label>

    <div :class="{ 'input-group': type === 'password' }">
      <input
        ref="inputRef"
        :id="id"
        :type="isCurrency ? 'text' : inputType"
        :inputmode="isCurrency ? 'numeric' : undefined"
        class="form-control"
        :class="{
          'is-invalid': internalErrors.length > 0,
          'form-control-sm': size === 'sm',
        }"
        :value="isCurrency ? displayValue : modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :style="effectiveInputStyle"
        @input="isCurrency ? handleCurrencyInput($event) : handleInput($event)"
        @blur="handleBlur"
        @focus="handleFocus"
      />

      <span
        v-if="type === 'password'"
        class="input-group-text"
        :class="{ 'border-danger': internalErrors.length > 0 }"
      >
        <button
          class="btn btn-link p-0 text-muted text-decoration-none"
          type="button"
          @click="togglePasswordVisibility"
          tabindex="-1"
          :disabled="disabled"
        >
          <i :class="isPasswordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
        </button>
      </span>
    </div>

    <div v-if="internalErrors.length > 0" class="invalid-feedback d-block mt-1">
      <div v-for="(error, index) in internalErrors" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  validateOnInput: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md'].includes(value),
  },
  inputStyle: {
    type: String,
    default: '',
  },
  isCurrency: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const internalErrors = ref([]);
const isPasswordVisible = ref(false);
const displayValue = ref('');
const numericValue = ref(null);
const digitsOnly = ref('');
const isInternalUpdate = ref(false);
const inputRef = ref(null);

const inputType = computed(() => {
  if (props.type === 'password') {
    return isPasswordVisible.value ? 'text' : 'password';
  }
  return props.type;
});

const effectiveInputStyle = computed(() => {
  if (props.isCurrency) {
    return props.inputStyle ? `text-align: right; ${props.inputStyle}` : 'text-align: right';
  }
  return props.inputStyle;
});

const formatCurrencyNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '';
  const [intPart, decPart] = num.toFixed(2).split('.');
  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decPart}`;
};

const formatFromDigits = (digits) => {
  // Siempre stripear ceros a la izquierda para evitar el bug
  const stripped = digits.replace(/^0+/, '');

  if (stripped === '') {
    return { formatted: '0.00', numeric: 0 };
  }
  
  // Cash register logic: últimos 2 dígitos = decimales
  if (stripped.length === 1) {
    const numeric = parseFloat(`0.0${stripped}`);
    return { formatted: '0.0' + stripped, numeric };
  } else if (stripped.length === 2) {
    const numeric = parseFloat(`0.${stripped}`);
    return { formatted: '0.' + stripped, numeric };
  } else {
    const intPart = stripped.slice(0, -2);
    const decPart = stripped.slice(-2);
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = `${formattedInt}.${decPart}`;
    const numeric = parseFloat(`${intPart}.${decPart}`);
    return { formatted, numeric };
  }
};

onMounted(() => {
  if (props.isCurrency) {
    const num = props.modelValue !== '' && props.modelValue !== null && props.modelValue !== undefined 
      ? parseFloat(String(props.modelValue).replace(/,/g, '')) 
      : 0;
    if (!isNaN(num)) {
      displayValue.value = formatCurrencyNumber(num);
      numericValue.value = num;
      digitsOnly.value = num === 0 ? '' : String(Math.round(num * 100));
    } else {
      displayValue.value = '0.00';
      numericValue.value = 0;
      digitsOnly.value = '';
    }
  }
});

const validate = (value) => {
  if (!props.rules || props.rules.length === 0) {
    internalErrors.value = [];
    return true;
  }

  const errors = [];
  for (const rule of props.rules) {
    const result = rule(value);
    // string  → mensaje de error (composables devuelven string o null)
    // false   → error genérico
    // null / true / undefined → válido
    if (typeof result === 'string' && result.length > 0) {
      errors.push(result);
    } else if (result === false) {
      errors.push('Campo inválido');
    }
  }
  internalErrors.value = errors;
  return errors.length === 0;
};

// Validar cuando cambia el valor desde afuera (tras el primer error, mantener validación activa)
watch(
  () => props.modelValue,
  (newVal) => {
    if (props.isCurrency) {
      // Si el cambio vino internamente (usuario escribiendo), no hacer nada
      if (isInternalUpdate.value) return;
      
      const incoming = (newVal !== null && newVal !== undefined && newVal !== '') 
        ? parseFloat(String(newVal).replace(/,/g, '')) 
        : 0;
      if (incoming !== numericValue.value) {
        if (!isNaN(incoming)) {
          displayValue.value = formatCurrencyNumber(incoming);
          numericValue.value = incoming;
          // Sincronizar digitsOnly solo cuando el valor cambia externamente
          digitsOnly.value = incoming === 0 ? '' : String(Math.round(incoming * 100));
        } else {
          displayValue.value = '0.00';
          numericValue.value = 0;
          digitsOnly.value = '';
        }
      }
      if (props.validateOnInput) validate(incoming);
    } else {
      if (props.validateOnInput) validate(newVal);
    }
  },
);

const handleInput = (event) => {
  const value = event.target.value;
  emit('update:modelValue', value);
  if (props.validateOnInput) {
    validate(value);
  }
};

const handleCurrencyInput = (event) => {
  const inputType = event.inputType;
  
  isInternalUpdate.value = true;
  
  if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
    // Borrar último dígito
    digitsOnly.value = digitsOnly.value.slice(0, -1);
  } else if (inputType === 'insertText' && event.data && /^[0-9]$/.test(event.data)) {
    // Insertar dígito
    digitsOnly.value += event.data;
  } else {
    // Fallback: extraer dígitos del valor completo
    const allDigits = event.target.value.replace(/[^0-9]/g, '');
    if (allDigits !== digitsOnly.value) {
      digitsOnly.value = allDigits;
    }
  }
  
  const { formatted, numeric } = formatFromDigits(digitsOnly.value);
  displayValue.value = formatted;
  numericValue.value = numeric;
  emit('update:modelValue', numeric);
  if (props.validateOnInput) validate(numeric);
  
  // Mover cursor al final después de actualizar
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.setSelectionRange(formatted.length, formatted.length);
    }
    isInternalUpdate.value = false;
  });
};

const handleFocus = () => {
  if (props.isCurrency && inputRef.value) {
    nextTick(() => {
      requestAnimationFrame(() => {
        if (inputRef.value) {
          const length = inputRef.value.value.length;
          inputRef.value.setSelectionRange(length, length);
        }
      });
    });
  }
};

const handleBlur = (event) => {
  if (props.isCurrency) {
    if (numericValue.value === null || numericValue.value === undefined) {
      displayValue.value = '0.00';
      numericValue.value = 0;
      emit('update:modelValue', 0);
    }
    validate(numericValue.value);
  } else {
    validate(event.target.value);
  }
};

const togglePasswordVisibility = () => {
  if (props.disabled) return;
  isPasswordVisible.value = !isPasswordVisible.value;
};
</script>

<style scoped>
.input-group-text {
  cursor: pointer;
}

.input-group-text .btn-link {
  color: #6c757d;
}

.input-group-text .btn-link:focus,
.input-group-text .btn-link:hover {
  color: #495057;
}
</style>
